'use strict';

/**
 * Overlay for choosing the location of one or multiple items
 *
 * @category   View
 * @module     collection.location
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var LayoutView = require('base/Colido.LayoutView');
var SelectMetaStoragesListView = require('../partials/select-meta-storages-list');
var SelectStoragesListView = require('../partials/select-storages-list');

var template = require('../../templates/layout/choose-storage-modal.hbs');

var ChooseStorageModalLayout = LayoutView.extend({
    template: template,

    regions: {
        locationsRegions: '#region-locations',
        storages0Region: '#region-storages-0',
        storages1Region: '#region-storages-1',
        storages2Region: '#region-storages-2',
        storages3Region: '#region-storages-3',
    },

    ui: {
        actionCancel: '[data-action="cancel"]',
        lanesComponent: '.vertical-lanes',
        lane: '.lane',
    },

    events: {
        'click @ui.actionCancel': 'onActionCancel',
        'click @ui.lane': 'onClickLane',
    },

    childEvents: {
        'storage:selected': 'onSelectStorage',
        'storage:goto': 'onGotoStorage',
        'storage:add': 'onAddStorage',
        'location:selected': 'onSelectLocation',
    },

    /**
     * Current level of listing
     * @type {number}
     */
    currentLevel: 0,

    /**
     * Max level
     * @type {number}
     */
    maxLevel: 3,

    /**
     * Should the messenger be showed?
     * @type {boolean}
     */
    showCreateMessage: false,

    /**
     * Number of effected objects (for header in template)
     * @type {number}
     */
    objectCount: 0,

    /**
     * Collection for the nested storages
     * @type {Colido.FilteredCollection}
     */
    nestedCollection: null,

    initialize: function(options) {
        LayoutView.prototype.initialize.apply(this, arguments);

        var catalog = App.CollectionModule.catalogs.getActiveCatalog();

        this.nestedCollection = catalog.storages.getNestedCollection();
    },

    onShow: function() {
        var catalog = App.CollectionModule.catalogs.getActiveCatalog();

        // Create locations view
        var viewLocation = new SelectMetaStoragesListView({
            collection: catalog.storages.getMetaStoragesCollection(),
            sortDefault: 'name.asc',
        });

        // Create storages view
        this.viewStorage = new SelectStoragesListView({
            collection: this.nestedCollection,
            level: 0,
            maxLevel: this.maxLevel,
            showCreateMessage: this.getOption('showCreateMessage'),
            sortDefault: 'subStorage.desc-name.asc',
        });

        // Show in regions
        this.locationsRegions.show(viewLocation);
        this.storages0Region.show(this.viewStorage);
    },

    /**
     * Closes the modal
     * @param {object} event The click event from the button
     */
    onActionCancel: function(event) {
        this.trigger('close:modal');
    },

    /**
     * Is triggered, when a lane is clicked. We will go back to the clicked lane
     * @param {object} event The click event
     */
    onClickLane: function(event) {
        var $curTarget = $(event.currentTarget);
        if (this.currentLevel > $curTarget.data('level') &&
            !$(event.target).parents('.list').length
        ) {
            this.setLevel($curTarget.data('level'));
        }
    },

    /**
     * Is triggered, when a location has been selected in child view
     * We will update the location of the selected items
     * @param {LocationModule.SelectLocationsLocationsListItemView} childView The child, that triggered the event
     * @param {number}                                              level     Level of the childview
     * @param {LocationModule.LocationModel}                        model     The relevant model
     */
    onSelectLocation: function(childView, level, storageModel) {
        if (level == this.currentLevel) {
            this.trigger('close:modal', {location: storageModel});
        } else if (level < this.currentLevel) {
            this.setLevel(level);
        }
    },

    /**
     * Is triggered, when a view should open substorages
     * We will update the storage information of the selected items
     * @param {LocationModule.SelectLocationsLocationsListItemView} childView The child, that triggered the event
     * @param {number}                                              level     Level of the childview
     * @param {LocationModule.LocationModel}                        model     The relevant model
     */
    onGotoStorage: function(childView, level, model) {
        if (level == this.currentLevel) {
            this.setLevel(level + 1, model);
        } else if (level < this.currentLevel) {
            this.setLevel(level);
        }
    },

    /**
     * Is triggered, when a storage has been selected in child view
     * We will update the storage information of the selected items
     * @param {LocationModule.SelectLocationsLocationsListItemView} childView The child, that triggered the event
     * @param {number}                                              level     Level of the childview
     * @param {LocationModule.LocationModel}                        model     The relevant model
     */
    onSelectStorage: function(childView, level, locationModel) {
        if (level == this.currentLevel) {
            this.trigger('close:modal', {location: locationModel});
        } else if (level < this.currentLevel) {
            this.setLevel(level);
        }
    },

    /**
     * Is triggered, when add storage has been selected in child view
     * We will update the storage information of the selected items
     * @param {LocationModule.SelectLocationsLocationsListItemView} childView The child, that triggered the event
     * @param {number}                                              level     Level of the childview
     * @param {LocationModule.LocationModel}                        model     The relevant model
     */
    onAddStorage: function(childView, level, model) {
        this.setLevel(level + 1, model, true);
    },

    /**
     * Sets a new level for the component
     * @param {number} newLevel The level to set
     * @param {object} model    (Optional) Related model for the new level
     */
    setLevel: function(newLevel, model, forceAdd) {
        if (!_.isBoolean(forceAdd)) {
            forceAdd = false;
        }

        // Destroy other regions
        if (newLevel < this.currentLevel) {
            var regionName;
            for (var i = Math.min(newLevel + 1, this.maxLevel); i <= this.maxLevel; i++) {
                regionName = 'storages' + i + 'Region';
                this[regionName].empty();
            }
        }

        // Is new level valid?
        if (newLevel <= this.maxLevel) {
            // Update ui
            this.currentLevel = newLevel;
            this.ui.lanesComponent.attr('data-level', newLevel).data('level', newLevel);

            // Do we have a model?
            if (model) {
                var catalog = model.collection.catalog;
                var subStorageCollection = catalog.storages.getFilteredCollection(function(collectionModel) {
                    return model.get('id') === collectionModel.get('parent');
                });

                var newRegion = 'storages' + newLevel + 'Region';
                var viewStorage = new SelectStoragesListView({
                    collection: subStorageCollection,
                    level: newLevel,
                    maxLevel: this.maxLevel,
                    model: model,
                    showCreateMessage: this.getOption('showCreateMessage'),
                    forceAdd: forceAdd,
                    sortDefault: 'subStorage.desc-name.asc',
                });

                this[newRegion].show(viewStorage);
            }
        }
    },
});

module.exports = ChooseStorageModalLayout;